// ONE PAGE, TWO PIPELINES, ONE ANSWER.
//
// ─── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
//
// The editor and the print PDF have disagreed about the same page at least
// five times: #1079 (a rotate left the thumbnail behind), #1094 (a rotate left
// the cached display URL behind — printed upside down), 2026-08-24 (a PDF
// older than its layout), B5 (four private copies of the slot maths), and
// #1198 on 2026-09-20 (the agent's `rotatePhoto` wrote a new print file and
// left the editor pointing at the old one).
//
// Each of those was fixed where it broke. None of them was fixed as a CLASS,
// so the next new writer of a photo file re-opened it. The shape is always the
// same and it is the house's oldest bug family: TWO READS OF "THE PHOTO",
// taken differently, compared never.
//
// The maths was never the problem. `pdfMirrorBox` (editor) and
// `computeTranslateDrawParams` (PDF) are algebraically the same function and
// `renderParity.test.ts` proves it on a matrix of cases. What diverges is the
// INPUT: which file each pipeline loads, and therefore what dimensions the
// cover-fit / fit-width decision is made from.
//
//   #1198 page 4, identical saved position {scale 1.37, rotation −90}:
//     editor  read upright.jpg     1400×787  → landscape → fit-width
//                                            → box 137.0% × 54.45%
//     print   read rot90-….jpg     1687×3000 → portrait  → cover-fill
//                                            → box 193.8% × 243.6%
//
//   Same numbers in, four and a half times the area out, because the two
//   pipelines were looking at two different files.
//
// So this module owns the comparison itself: convert a PDF draw rect into the
// editor's percentage space, and say — in points on the real page — whether
// the two pipelines put the photo in the same place. The test uses it as a
// deploy gate; `assertSameOrientation` uses the same idea at render time so a
// book that WOULD print wrong fails loudly instead of shipping.

import type { Rect } from './slotGeometry';

/** The editor's box: percentages of the slot, y-DOWN (CSS). */
export interface SlotBoxPct { left: number; top: number; width: number; height: number }

/** The PDF's draw rect: absolute points, y-UP (pdf-lib). */
export interface PdfDrawRect { x: number; y: number; width: number; height: number }

/**
 * Put a PDF draw rect into the editor's space so the two can be compared.
 *
 * pdf-lib measures from the page's BOTTOM-LEFT and CSS from the slot's
 * TOP-LEFT, so the vertical term is the slot's top edge minus the box's top
 * edge — not a sign flip on y, which is the mistake that makes a "parity"
 * check pass on two different layouts.
 */
export function pdfRectToSlotPct(rect: PdfDrawRect, slot: Rect): SlotBoxPct {
  return {
    left: ((rect.x - slot.x) / slot.w) * 100,
    top: ((slot.y + slot.h - (rect.y + rect.height)) / slot.h) * 100,
    width: (rect.width / slot.w) * 100,
    height: (rect.height / slot.h) * 100,
  };
}

/**
 * How far apart the two pipelines put the photo, in POINTS on the real page.
 *
 * ⚠️ POINTS, NOT PERCENT. A percentage tolerance means something different on
 * a full-page 1-up and on a quarter-page 4-up slot, and the thing that matters
 * is how far the print is from the picture the customer approved — which is a
 * physical distance. 1 pt is 0.35 mm.
 */
export function boxGapPt(a: SlotBoxPct, b: SlotBoxPct, slot: Rect): number {
  const dx = Math.abs(a.left - b.left) * slot.w / 100;
  const dy = Math.abs(a.top - b.top) * slot.h / 100;
  const dw = Math.abs(a.width - b.width) * slot.w / 100;
  const dh = Math.abs(a.height - b.height) * slot.h / 100;
  return Math.max(dx, dy, dw, dh);
}

/**
 * Tolerance for a page that is "the same page".
 *
 * ⚠️ IT CANNOT BE ZERO, AND THE REASON IS NOT SLOPPINESS. The editor measures
 * the image the browser loaded — the 1400 px display tier — and the PDF
 * measures the full-resolution original. Resizing 3000×1687 down to 1400×787
 * rounds the aspect ratio (1.77830 → 1.77890), which moves the drawn box by
 * about 0.15 pt on an A4 page. Demanding exact equality would fail every
 * correct render; demanding a visible distance catches every wrong one. The
 * #1198 divergence was 280 pt.
 */
export const PARITY_TOL_PT = 1;

/** Human sentence for a failure — the test's message, and an alert's body. */
export function parityMessage(
  what: string, editor: SlotBoxPct, pdf: SlotBoxPct, slot: Rect,
): string {
  const n = (v: number) => v.toFixed(2);
  return `${what}: editor ${n(editor.width)}%×${n(editor.height)}% at `
    + `(${n(editor.left)}, ${n(editor.top)}) — PDF ${n(pdf.width)}%×${n(pdf.height)}% at `
    + `(${n(pdf.left)}, ${n(pdf.top)}) — ${boxGapPt(editor, pdf, slot).toFixed(1)} pt apart`;
}

// ─── THE FILE, NOT THE MATHS ────────────────────────────────────────────────

/**
 * The storage object a tier URL points at, or null when it names no object.
 *
 * Handles both Supabase shapes — `/object/sign/<bucket>/<path>` and the
 * transform shape `/render/image/sign/<bucket>/<path>` — and returns the path
 * WITHOUT the bucket, so a display tier and a print tier of the same photo
 * compare equal however they were signed.
 *
 * A Cloudflare Images delivery URL names no Supabase object and returns null:
 * it is a converted copy (the HEIC path), and whether it is current cannot be
 * read off the URL. That is exactly why the repoint trigger nulls it.
 */
export function storageObjectOf(url: string | null | undefined): string | null {
  const u = String(url ?? '');
  if (!u) return null;
  const m = u.match(/\/storage\/v1\/(?:object|render\/image)\/(?:sign|public|authenticated)\/[^/]+\/([^?]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Every tier of one photo row, as the API and the DB hold them. */
export interface PhotoTiers {
  id?: string;
  storage_path?: string | null;
  preview_storage_path?: string | null;
  thumbnail_storage_path?: string | null;
  display_url?: string | null;
  tray_url?: string | null;
}

/** The photo's own folder — every tier of one photo is stored beside it. */
function folderOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

/**
 * Null when nothing about this photo's tiers contradicts the file the PDF will
 * print; otherwise the sentence saying which one does.
 *
 * ⚠️ READ THIS BEFORE TIGHTENING IT. The obvious rule — "every tier must name
 * `storage_path`" — is WRONG here and a scan written to it reports 563 false
 * alarms. Since migration 128 the editor's two tiers are signed against the
 * browser-made `thumbnail.jpg` / `preview.jpg` beside the original, on purpose,
 * to avoid paying for image transforms. Different FILES, same picture: that is
 * the design and it must stay.
 *
 * What a path really can prove is only this, and the function claims no more:
 *
 *   1. a tier outside this photo's own folder belongs to another photo — as
 *      ten live #1205-shaped rows still do, where the upright bake moved the
 *      print file into `sessions/…` and left the display tier on the raw
 *      `whatsapp/…` upload;
 *   2. once the print file is a ROTATED copy (`rot90-…jpg`), every sibling in
 *      the folder was made from the picture before the turn, so any tier that
 *      is not that exact file is stale — which is #1198, where `display_url`
 *      still served `upright.jpg`;
 *   3. a Cloudflare Images copy beside a rotated print file is the same story
 *      with a URL that names no Supabase object at all.
 *
 * A stale `thumbnail.jpg` sitting beside a fresh original is invisible to any
 * scan — same folder, same name. That is precisely why the enforcement is
 * migration 169's trigger, at the moment of the write, and this is an auditor
 * for what escaped before it existed.
 */
export function photoTierMismatch(photo: PhotoTiers): string | null {
  const print = String(photo.storage_path ?? '').trim();
  if (!print) return null;                 // nothing to print from — a different alarm
  const printFolder = folderOf(print);
  const rotatedPrint = /\/rot(90|180|270)-\d+\.jpg$/.test(print);

  const tiers: Array<[string, string | null | undefined]> = [
    ['display_url', photo.display_url],
    ['tray_url', photo.tray_url],
    ['preview_storage_path', photo.preview_storage_path],
    ['thumbnail_storage_path', photo.thumbnail_storage_path],
  ];
  for (const [name, value] of tiers) {
    const v = String(value ?? '').trim();
    if (!v) continue;                      // a missing tier is re-derived on next load
    const obj = v.startsWith('http') ? storageObjectOf(v) : v;
    if (obj === null) {
      // A Cloudflare Images copy — it names no Supabase object, so the only
      // thing knowable is the company it keeps.
      if (rotatedPrint) return `${name} is a Cloudflare Images copy while storage_path is a rotated file (${print})`;
      continue;
    }
    if (folderOf(obj) !== printFolder) {
      return `${name} points at ${obj}, which is not this photo's folder (${printFolder})`;
    }
    // A server-made tier (worker lib/serverTiers.ts) is named after the exact
    // file it was made from — `<print>.srvtier-….jpg` — and stamped only while
    // that file is the print file: made after the rotate, not before it.
    if (rotatedPrint && obj !== print && !obj.startsWith(`${print}.srvtier-`)) {
      return `${name} points at ${obj}, made before the rotate; the PDF prints ${print}`;
    }
  }
  return null;
}

/**
 * Throw when the file the PDF just embedded is not even the same SHAPE as the
 * one the editor measured. Called from the renderer, per slot.
 *
 * ⚠️ ORIENTATION, NOT EQUALITY. Tiers legitimately differ by a rounding
 * epsilon (see PARITY_TOL_PT), so an equality check would fail every correct
 * render. What no resize can ever do is turn a landscape into a portrait — and
 * that flip is precisely what moves a photo from the fit-width branch to the
 * cover-fill branch and reframes the whole page. It is the #1198 failure, and
 * it is worth stopping a render for: a failed render is a queue entry, a
 * wrong-shaped page is a printed book.
 */
export function assertSameOrientation(
  photoId: string, dbW: number | null | undefined, dbH: number | null | undefined,
  fileW: number, fileH: number,
): void {
  if (!dbW || !dbH || !fileW || !fileH) return;     // unknown is not evidence
  const dbLandscape = dbW > dbH;
  const fileLandscape = fileW > fileH;
  if (dbLandscape === fileLandscape) return;
  throw new Error(
    `PHOTO_FILE_MISMATCH ${photoId}: the editor measured ${dbW}×${dbH} `
    + `(${dbLandscape ? 'landscape' : 'portrait'}) and the print file is ${fileW}×${fileH} `
    + `(${fileLandscape ? 'landscape' : 'portrait'}). Two different files for one photo — `
    + 'refusing to print a page the customer never saw.',
  );
}
